import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateEvent } from "../../services/eventService";
import type { Event, UpdateEventInput } from "../../types/event";

/**
 * Phase 37 — PUT /api/events/:id mutation hook.
 *
 * Variables: { id, patch } — id MUST be a bare UUID (caller extracts baseId
 * via `parseSyntheticEventId(event.id)?.baseId ?? event.id` per RESEARCH Pitfall 2).
 * EDM-05: edit operates whole-series for v1.11.
 *
 * On success: predicate-invalidates every cached `['events', ...]` query
 * (C-04 / INTG-05). No optimistic updates (D-03).
 * Toast / analytics are caller-side (onSuccess/onError overrides at form call site).
 */
export function useUpdateEvent() {
	const qc = useQueryClient();
	return useMutation<Event, Error, { id: string; patch: UpdateEventInput }>({
		mutationFn: ({ id, patch }) => updateEvent(id, patch),
		onSuccess: () => {
			qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "events" });
		},
		retry: 0,
	});
}
